import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import api from '../api/client';

const AuthContext = createContext(null);

function useProvideAuth() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [impersonation, setImpersonation] = useState(null);

  const checkAuth = useCallback(async (options = {}) => {
    const { suppressError = true } = options;
    try {
      setIsLoading(true);
      const response = await api.auth.getSession();
      setUser(response.user);
      setImpersonation(response.impersonation || null);
      setError(null);
      return response.user;
    } catch (err) {
      setUser(null);
      setImpersonation(null);
      setError(err.message);
      if (!suppressError) {
        throw err;
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signIn = useCallback(async (credentials) => {
    try {
      setIsLoading(true);
      const response = await api.auth.signIn(credentials);
      setUser(response.user);
      setError(null);
      return { success: true, user: response.user };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.auth.signOut();
      setUser(null);
      setImpersonation(null);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const stopImpersonation = useCallback(async () => {
    try {
      await api.auth.stopImpersonation();
      await checkAuth({ suppressError: false });
    } catch (err) {
      setError(err.message);
    }
  }, [checkAuth]);

  useEffect(() => {
    checkAuth({ suppressError: true });
  }, [checkAuth]);

  const value = useMemo(
    () => ({
      user,
      isLoading,
      error,
      signIn,
      signOut,
      stopImpersonation,
      checkAuth,
      impersonation,
    }),
    [user, isLoading, error, signIn, signOut, stopImpersonation, checkAuth, impersonation]
  );

  return value;
}

export function AuthProvider({ children }) {
  const auth = useProvideAuth();
  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Authentication hook
 * Consumes global auth context provided by AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
